import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import {
  ArrowLeft,
  LogOut,
  Store as StoreIcon,
  ShoppingBag,
  Loader2,
  MapPin,
  ClipboardList,
  RotateCcw,
} from "lucide-react";
import { customerCustomFetch } from "@workspace/api-client-react";
import type {
  CustomerStoreSummary,
  CustomerAddress,
} from "@workspace/api-client-react";
import { useCustomerAuth } from "@/hooks/use-customer-auth";
import { formatCurrency } from "@/lib/utils";
import { PontosDaConta } from "@/components/catalog/PontosDaConta";
import { diaEHora, guardarPedidoDeNovo, resumoDosItens, statusParaOCliente, type PedidoDoCliente } from "@/lib/pedir-de-novo";

function useQueryParam(name: string): string | null {
  const search = useSearch();
  return useMemo(() => {
    if (!search) return null;
    const qs = search.startsWith("?") ? search.slice(1) : search;
    if (!qs) return null;
    const params = new URLSearchParams(qs);
    return params.get(name);
  }, [search, name]);
}

export default function CustomerAccount() {
  const { customer, isLoading, logout } = useCustomerAuth();
  const [, navigate] = useLocation();
  const storeSlug = useQueryParam("store");

  const [stores, setStores] = useState<CustomerStoreSummary[] | null>(null);
  const [loadingStores, setLoadingStores] = useState(true);
  const [storesError, setStoresError] = useState<string | null>(null);

  const [orders, setOrders] = useState<PedidoDoCliente[] | null>(null);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState<string | null>(null);

  const [addresses, setAddresses] = useState<CustomerAddress[] | null>(null);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [addressesError, setAddressesError] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading) return;
    if (!customer) {
      navigate("/");
      return;
    }
    let cancelled = false;
    setLoadingStores(true);
    customerCustomFetch<CustomerStoreSummary[]>("/api/customer/stores")
      .then((res) => {
        if (cancelled) return;
        setStores(Array.isArray(res) ? res : []);
        setStoresError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setStoresError("Não foi possível carregar suas lojas.");
      })
      .finally(() => {
        if (!cancelled) setLoadingStores(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customer, isLoading, navigate]);

  useEffect(() => {
    if (!customer || !storeSlug) {
      setOrders(null);
      setAddresses(null);
      return;
    }
    let cancelled = false;
    setLoadingOrders(true);
    setLoadingAddresses(true);

    customerCustomFetch<PedidoDoCliente[]>(`/api/catalog/${storeSlug}/orders`)
      .then((res) => {
        if (cancelled) return;
        setOrders(Array.isArray(res) ? res : []);
        setOrdersError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setOrdersError("Não foi possível carregar seus pedidos.");
        setOrders([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingOrders(false);
      });

    customerCustomFetch<CustomerAddress[]>(`/api/catalog/${storeSlug}/addresses`)
      .then((res) => {
        if (cancelled) return;
        setAddresses(Array.isArray(res) ? res : []);
        setAddressesError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setAddressesError("Não foi possível carregar seus endereços.");
        setAddresses([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingAddresses(false);
      });

    return () => {
      cancelled = true;
    };
  }, [customer, storeSlug]);

  const currentStore = storeSlug
    ? stores?.find((s) => s.slug === storeSlug) ?? null
    : null;
  const backHref = storeSlug
    ? currentStore?.storeType === "salao"
      ? `/agendar/${storeSlug}`
      : `/loja/${storeSlug}`
    : "/";

  // Salão agenda serviço: não tem carrinho para encher.
  const podePedirDeNovo = !!storeSlug && currentStore?.storeType !== "salao";

  const handleLogout = async () => {
    await logout();
    navigate(backHref);
  };

  if (isLoading) {
    return (
      <div className="min-h-[100svh] flex items-center justify-center bg-gray-50">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!customer) return null;

  return (
    <div className="min-h-[100svh] bg-gray-50 pb-12">
      <header className="sticky top-0 z-40 bg-card shadow-sm border-b border-border/50">
        <div className="px-4 py-3 max-w-3xl mx-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              if (typeof window !== "undefined" && window.history.length > 1) {
                window.history.back();
              } else {
                navigate(backHref);
              }
            }}
            aria-label="Voltar"
            className="p-2 -ml-2 rounded-full hover:bg-muted text-muted-foreground transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-bold text-foreground">Minha conta</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <section className="bg-card rounded-2xl border border-border/50 p-5 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-2xl border border-border/50 overflow-hidden">
              {customer.avatarBase64 ? (
                <img
                  src={customer.avatarBase64.startsWith("data:") ? customer.avatarBase64 : `data:image/jpeg;base64,${customer.avatarBase64}`}
                  alt={customer.firstName}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span>{(customer.firstName || "?").charAt(0).toUpperCase()}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-foreground truncate">
                {customer.firstName || "Cliente"}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                {customer.whatsappFormattedNumber || `+${customer.whatsappNumber}`}
              </p>
            </div>
          </div>
        </section>

        {storeSlug && (
          <>
            <PontosDaConta storeSlug={storeSlug} storeName={currentStore?.name ?? null} />

            <section className="bg-card rounded-2xl border border-border/50 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <ClipboardList className="w-5 h-5 text-muted-foreground" />
                <h2 className="font-semibold text-foreground">
                  {currentStore ? `Pedidos em ${currentStore.name}` : "Pedidos desta loja"}
                </h2>
              </div>
              {loadingOrders ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Carregando pedidos...
                </div>
              ) : ordersError ? (
                <p className="text-sm text-destructive">{ordersError}</p>
              ) : orders && orders.length > 0 ? (
                <ul className="divide-y divide-border/60">
                  {orders.slice(0, 10).map((order) => (
                    <li key={order.id} className="py-3 first:pt-0 last:pb-0" data-testid={`conta-pedido-${order.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate" data-testid="conta-pedido-quando">
                            {diaEHora(order.createdAt, order.storeTimezone)} · {statusParaOCliente(order.status)}
                          </p>
                          <p className="text-xs text-muted-foreground line-clamp-2" data-testid="conta-pedido-itens">
                            {resumoDosItens(order.items)}
                          </p>
                        </div>
                        <p className="font-semibold text-foreground whitespace-nowrap">
                          {formatCurrency(Number(order.total))}
                        </p>
                      </div>
                      {podePedirDeNovo && order.items.length > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            guardarPedidoDeNovo(storeSlug, order.items);
                            navigate(`/loja/${storeSlug}`);
                          }}
                          className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
                          data-testid="conta-pedir-de-novo"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                          Pedir de novo
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Você ainda não fez pedidos nesta loja.
                </p>
              )}
            </section>

            <section className="bg-card rounded-2xl border border-border/50 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-muted-foreground" />
                <h2 className="font-semibold text-foreground">Endereços salvos</h2>
              </div>
              {loadingAddresses ? (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Carregando endereços...
                </div>
              ) : addressesError ? (
                <p className="text-sm text-destructive">{addressesError}</p>
              ) : addresses && addresses.length > 0 ? (
                <ul className="divide-y divide-border/60">
                  {addresses.map((addr) => (
                    <li key={addr.id} className="py-3 first:pt-0 last:pb-0">
                      <p className="font-medium text-foreground">
                        {addr.street}
                        {addr.number ? `, ${addr.number}` : ""}
                        {addr.isDefault && (
                          <span className="ml-2 text-xs font-normal text-primary">(padrão)</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {[addr.neighborhood, addr.city].filter(Boolean).join(" · ")}
                        {addr.zipCode ? ` · CEP ${addr.zipCode}` : ""}
                      </p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Você ainda não tem endereços salvos.
                </p>
              )}
            </section>
          </>
        )}

        <section className="bg-card rounded-2xl border border-border/50 p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <StoreIcon className="w-5 h-5 text-muted-foreground" />
            <h2 className="font-semibold text-foreground">Lojas que você usa</h2>
          </div>

          {loadingStores ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Carregando lojas...
            </div>
          ) : storesError ? (
            <p className="text-sm text-destructive">{storesError}</p>
          ) : stores && stores.length > 0 ? (
            <ul className="divide-y divide-border/60">
              {stores.map((store) => {
                const path = store.storeType === "salao" ? `/agendar/${store.slug}` : `/loja/${store.slug}`;
                return (
                  <li key={store.id} className="py-3 first:pt-0 last:pb-0">
                    <Link
                      href={path}
                      className="flex items-center gap-3 hover:bg-muted/40 -mx-2 px-2 py-2 rounded-lg transition-colors"
                    >
                      {store.logoUrl ? (
                        <img
                          src={store.logoUrl}
                          alt={store.name}
                          className="w-10 h-10 rounded-full object-cover border border-border/50"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                          {store.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground truncate">{store.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {store.storeType === "salao" ? "Agendamentos" : "Pedidos"}
                        </p>
                      </div>
                      <ShoppingBag className="w-4 h-4 text-muted-foreground" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">
              Você ainda não fez pedidos ou agendamentos em nenhuma loja.
            </p>
          )}
        </section>

        <button
          type="button"
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 bg-card border border-border/50 hover:bg-muted/40 text-foreground rounded-2xl py-3 font-semibold transition-colors shadow-sm"
        >
          <LogOut className="w-4 h-4" />
          Sair
        </button>
      </main>
    </div>
  );
}
